import { memo } from "react";
import type { NormValue, SpreadFields } from "../../helpers/extractTrade";
import CardSourceToggle from "./CardSourceToggle";
import {
  ActionBadge,
  AxisBar,
  CardHeaderBar,
  CardShell,
  DetailRows,
  Divider,
  ExpiryPill,
  HeadlineStats,
  LegRow,
  NetValue,
  SignalPill,
  fmtExpiryShort,
  fmtMoney,
  num,
  type DetailRow,
  type HeadlineStat,
} from "./trade";

type SpreadCardProps = {
  fields: SpreadFields;
  /**
   * The exact source text this card was extracted from. `fields` is a pure
   * function of it, so memo() compares this one string instead of the
   * (freshly rebuilt every segmentation) object.
   */
  raw?: string;
  companyName?: string;
};

// "Jun 21" — month/day for a leg row (the year lives in the header pill).
const legExpiry = (v: NormValue): string | undefined =>
  fmtExpiryShort(v)?.replace(/ '\d+$/, "") ?? undefined;

const SpreadCard = ({ fields, raw, companyName }: SpreadCardProps) => {
  const { ticker, direction } = fields;
  const signalTone = direction === "bullish" ? "bull" : direction === "bearish" ? "bear" : "neutral";

  const shortLeg = fields.legs.find(l => l.role === "short") ?? fields.legs[0];
  const longLeg = fields.legs.find(l => l.role === "long") ?? fields.legs[1];

  const shortStrike = num(shortLeg?.strike);
  const longStrike = num(longLeg?.strike);
  const spot = num(fields.current_price);
  const breakeven = num(fields.breakeven);
  const maxLoss = num(fields.max_loss);
  const maxGain = num(fields.max_gain);

  const expiryPill = fmtExpiryShort(shortLeg?.expiry ?? longLeg?.expiry) ?? null;

  // A debit spread is bought, a credit spread is sold (colours the verb chip).
  const verb = fields.netKind === "debit" ? "Buy" : fields.netKind === "credit" ? "Sell" : undefined;

  // Net premium per contract, signed: a debit costs, a credit pays.
  const netStr = fields.net != null
    ? `${fields.netKind === "debit" ? "-" : "+"}${fmtMoney(fields.net)} / contract`
    : null;

  const hasHeadline = maxGain != null || maxLoss != null || breakeven != null;
  const headline: HeadlineStat[] = [
    { label: "Max profit", value: maxGain != null ? fmtMoney(maxGain, 0) : "—", tone: "success" },
    { label: "Max loss", value: maxLoss != null ? `-${fmtMoney(Math.abs(maxLoss), 0)}` : "—", tone: "error" },
    { label: "Break-even", value: breakeven != null ? fmtMoney(breakeven) : "—", tone: "primary" },
  ];

  const detailCandidates: (DetailRow | null)[] = [
    fields.net != null
      ? {
          label: fields.netKind === "credit" ? "Entry Credit" : "Entry Debit",
          value: `${fmtMoney(fields.net)} / contract`,
          tone: "primary",
        }
      : null,
    maxGain != null ? { label: "Max Profit", value: `${fmtMoney(maxGain, 0)} total`, tone: "success" } : null,
    maxLoss != null ? { label: "Max Loss", value: `${fmtMoney(Math.abs(maxLoss), 0)} total`, tone: "error" } : null,
    fields.pop && /\d/.test(fields.pop) ? { label: "Prob. of profit", value: fields.pop, tone: "primary" } : null,
  ];
  const detailRows = detailCandidates.filter((r): r is DetailRow => r != null);

  return (
    <CardShell>
      <div className="flex flex-col gap-4">
        <CardHeaderBar
          ticker={ticker}
          left={(
            <>
              {direction && <SignalPill label={direction === "bullish" ? "Bullish" : "Bearish"} tone={signalTone} />}
              <ActionBadge verb={verb} instrument="Spread" />
            </>
          )}
          right={expiryPill ? <ExpiryPill label={expiryPill} /> : undefined}
        />

        <Divider />

        {/* "Bull Call Spread · SPDR S&P 500 ETF" + the LLM's risk phrasing */}
        {(fields.strategy || companyName || fields.risk_reward) && (
          <div className="flex flex-col gap-1">
            {(fields.strategy || companyName) && (
              <div className="text-lg font-bold text-token-text-primary">
                {fields.strategy}
                {companyName && <span className="font-normal text-token-text-secondary">{`  ·  ${companyName}`}</span>}
              </div>
            )}
            {fields.risk_reward && <p className="text-sm text-token-text-secondary">{fields.risk_reward}</p>}
          </div>
        )}

        {/* legs + net premium */}
        {fields.legs.length > 0 && (
          <>
            <Divider />
            <section className="flex flex-col gap-2">
              {fields.legs.map((leg, idx) => {
                const strike = num(leg.strike);
                const cp = leg.call_put ? String(leg.call_put) : undefined;
                const premium = num(leg.premium);
                return (
                  <LegRow
                    key={idx}
                    side={leg.buy_sell ? String(leg.buy_sell) : leg.role === "short" ? "Sell" : "Buy"}
                    contract={[strike != null ? fmtMoney(strike, strike % 1 === 0 ? 0 : 2) : null, cp].filter(Boolean).join(" ")}
                    expiry={legExpiry(leg.expiry)}
                    type={cp?.toUpperCase()}
                    quantity="1x"
                    premium={premium != null ? fmtMoney(premium) : undefined}
                  />
                );
              })}
              {netStr && <NetValue kind={fields.netKind === "credit" ? "credit" : "debit"} amount={netStr} />}
            </section>
          </>
        )}

        {/* short / break-even / long / spot axis */}
        <Divider />
        <AxisBar
          columns={[
            { label: "Short", value: shortStrike != null ? fmtMoney(shortStrike) : "—", tone: "error" },
            { label: "B/E", value: breakeven != null ? fmtMoney(breakeven) : "—", tone: "secondary" },
            { label: "Long", value: longStrike != null ? fmtMoney(longStrike) : "—", tone: "success" },
            { label: "Spot", value: spot != null ? fmtMoney(spot) : "—", tone: "secondary" },
          ]}
        />

        {/* headline economics: max profit / max loss / break-even */}
        {hasHeadline && (
          <>
            <Divider />
            <HeadlineStats items={headline} />
          </>
        )}

        {/* entry · max profit · max loss · prob. of profit */}
        {detailRows.length > 0 && (
          <>
            <Divider />
            <DetailRows rows={detailRows} />
          </>
        )}
      </div>

      {/* full source text — caveats the fields can't represent live here */}
      <CardSourceToggle raw={raw} />
    </CardShell>
  );
};

// Skip re-rendering while a sibling block streams: segmentMessage rebuilds the
// fields object on every SSE chunk, but it derives entirely from `raw` — equal
// raw ⇒ identical card. No raw (other callers) ⇒ always re-render.
const propsAreEqual = (prev: SpreadCardProps, next: SpreadCardProps) =>
  prev.raw != null
  && prev.raw === next.raw
  && prev.companyName === next.companyName;

export default memo(SpreadCard, propsAreEqual);
